// Import configData
import configData from '../../../configData.json';

export const calculateOpeningTimes = (setDisplayOpen, hours = false) => {
	const currentDate = new Date();
	const currentDay = currentDate.getDay();
	const timestamp = currentDate.toLocaleTimeString('en-GB', { timeZone: 'Europe/London' });

	const formatTime = (timeString) => {
		if (!timeString || typeof timeString !== 'string') return '';
		// Handle both "HH:MM" and "HH:MM:SS" formats
		const parts = timeString.split(':');
		const hours = parseInt(parts[0], 10);
		const minutes = parts[1] || '00';
		const hourToShow = hours % 12 || 12;
		const minutesToShow = minutes === '00' ? '' : `:${minutes}`;
		const ampm = hours < 12 ? 'am' : 'pm';
		return `${hourToShow}${minutesToShow}${ampm}`;
	};

	const getDay = (day, open, closed, tomorrowStartHour = null, reopenDayName = null, todayStartHour = null, tomorrowInArray = false) => {
		// Check if open/closed times are set (not null)
		const isValidTime = open && closed && typeof open === 'string' && typeof closed === 'string' && open.trim() !== '' && closed.trim() !== '';
		
		// Normalize time formats for comparison 
		const normalizeTime = (timeStr) => {
			if (!timeStr || typeof timeStr !== 'string') return '';
			return timeStr.split(':').length === 2 ? `${timeStr}:00` : timeStr;
		};
		
		if (isValidTime && timestamp >= normalizeTime(open) && timestamp <= normalizeTime(closed)) {
			const timeToShow = formatTime(closed);
			setDisplayOpen(`Open today until ${timeToShow}`);
		} else {
			// If time is before opening time today and todayStartHour is provided, use it
			if (isValidTime && timestamp < normalizeTime(open) && todayStartHour && typeof todayStartHour === 'string' && todayStartHour.trim() !== '') {
				const timeToShow = formatTime(todayStartHour);
				setDisplayOpen(`Closed - reopens ${timeToShow}`);
			}
			// If tomorrow exists in array but startHour is not provided (null), show "Closed"
			else if (tomorrowInArray && (!tomorrowStartHour || typeof tomorrowStartHour !== 'string' || tomorrowStartHour.trim() === '')) {
				setDisplayOpen(`Closed`);
			}
			// If tomorrow's start hour is provided, use it
			else if (tomorrowStartHour && typeof tomorrowStartHour === 'string' && tomorrowStartHour.trim() !== '') {
				const timeToShow = formatTime(tomorrowStartHour);
				const reopenText = reopenDayName ? reopenDayName : 'tomorrow';
				setDisplayOpen(`Closed - reopens ${reopenText} ${timeToShow}`);
			} else {
				(day === 6 || day === 7)
					? setDisplayOpen(`Closed - reopens Monday 9am`)
					: setDisplayOpen(`Closed - reopens 9am`);
			}
		}
	}; 

	const checkDateHours = (hours, currentDate, daysOffset = 0) => {
		// Check if hours and hours.hours array exist and has items
		if(!hours || !hours.hours || !Array.isArray(hours.hours) || hours.hours.length === 0) {
			return false;
		}
		
		// Calculate target date
		const targetDate = new Date(currentDate);
		targetDate.setDate(targetDate.getDate() + daysOffset);
		const targetDateString = targetDate.toISOString().split('T')[0];
		
		// Find matching date in the opening times array
		return hours.hours.find(hour => {
			if (!hour.date) return false;
			const hourDateString = hour.date.split('T')[0];
			return hourDateString === targetDateString;
		}) || false;
	}

	const handleTodayHours = (day, defaultHours) => {
		// First check if today's date exists in the opening times array
		const dateInArray = checkDateHours(hours, currentDate, 0);
		// Check if tomorrow exists in the opening times array
		const tomorrowInArray = checkDateHours(hours, currentDate, 1);
		const tomorrowStartHour = tomorrowInArray?.startHour || null;
		const tomorrowExists = !!tomorrowInArray;
		
		if(dateInArray) {
			// Use hours from the array if found
			const { startHour: open, endHour: closed } = dateInArray;
			if (open === null || closed === null) {
				setDisplayOpen(`Closed Today`);
				return;
			}
			getDay(day, open, closed, tomorrowStartHour, null, open, tomorrowExists);
		} else {
			// Fall back to default hours from configData if not in array
			getDay(day, defaultHours.open, defaultHours.close, tomorrowStartHour, null, null, tomorrowExists);
		}
	}

	if (currentDay >= 1 && currentDay <= 4) {
		//Weekday Logic
		handleTodayHours(currentDay, configData.opening_times.weekdays);
	} else if (currentDay === 5) {
		//Friday Logic
		handleTodayHours(currentDay, configData.opening_times.friday);
	} else if (currentDay === 6) {
		//Saturday Logic
		const todayInArray = checkDateHours(hours, currentDate, 0);
		const mondayInArray = checkDateHours(hours, currentDate, 2);
		const mondayStartHour = mondayInArray?.startHour || null;
		const mondayExists = !!mondayInArray;
		
		if(todayInArray) {
			const { startHour: open, endHour: closed } = todayInArray;
			if (open === null || closed === null) {
				setDisplayOpen(`Closed Today`);
				return;
			}
			getDay(currentDay, open, closed, mondayStartHour, 'Monday', open, mondayExists);
		} else {
			getDay(currentDay, configData.opening_times.saturday.open, configData.opening_times.saturday.close, mondayStartHour, 'Monday', null, mondayExists);
		}
	} else {
		//Sunday Logic
		const todayInArray = checkDateHours(hours, currentDate, 0);
		const mondayInArray = checkDateHours(hours, currentDate, 1);
		const mondayStartHour = mondayInArray?.startHour || null;
		const mondayExists = !!mondayInArray;
		
		if(todayInArray) {
			const { startHour: open, endHour: closed } = todayInArray;
			if (open === null || closed === null) {
				setDisplayOpen(`Closed Today`);
				return;
			}
			getDay(currentDay, open, closed, mondayStartHour, null, open, mondayExists);
		} else {
			getDay(currentDay, configData.opening_times.sunday.open, configData.opening_times.sunday.close, mondayStartHour, null, null, mondayExists);
		}
	}
};
